import React, { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TrendingUp,
  TrendingDown,
  BarChart3,
} from "lucide-react";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  Scatter,
  ZAxis,
} from "recharts";

// Common currency pairs for the selector
const POPULAR_PAIRS = [
  "EUR/USD",
  "GBP/USD",
  "USD/JPY",
  "USD/CHF",
  "AUD/USD",
  "NZD/USD",
  "USD/CAD",
  "EUR/GBP",
  "EUR/JPY",
  "GBP/JPY",
  "XAU/USD",
  "BTC/USD",
];

const TIMEFRAMES = [
  { value: "1D", label: "1D", days: 1 },
  { value: "1W", label: "1W", days: 7 },
  { value: "1M", label: "1M", days: 30 },
];

// Generate realistic OHLC data from trades
function generateOHLCData(trades, pair, timeframe) {
  if (!trades || trades.length === 0) {
    return generateRandomOHLC(pair, timeframe);
  }

  // Filter trades for the selected pair
  const pairTrades = trades.filter(
    (t) => t.currency_pair?.toUpperCase() === pair.toUpperCase()
  );

  if (pairTrades.length === 0) {
    return generateRandomOHLC(pair, timeframe);
  }

  // Group trades by date
  const grouped = {};
  pairTrades.forEach((trade) => {
    const date = trade.execution_date;
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(trade);
  });

  // Convert to OHLC format
  const days = timeframe === "1D" ? 1 : timeframe === "1W" ? 7 : 30;
  const data = [];
  const sortedDates = Object.keys(grouped).sort();

  // Take only recent dates based on timeframe
  const recentDates = sortedDates.slice(-days * 2); // Extra buffer

  let basePrice = getBasePrice(pair);

  recentDates.forEach((date, i) => {
    const dayTrades = grouped[date];
    const dailyPnL = dayTrades.reduce((sum, t) => sum + (t.profit_loss || 0), 0);
    const volatility = Math.abs(dailyPnL) * 0.001 + Math.random() * 0.002;

    const open = basePrice;
    const close = basePrice + dailyPnL * 0.0001;
    const high = Math.max(open, close) + volatility * basePrice;
    const low = Math.min(open, close) - volatility * basePrice;
    const volume = dayTrades.reduce((sum, t) => sum + (t.risk_amount || 0), 0);

    data.push({
      date,
      open: parseFloat(open.toFixed(5)),
      high: parseFloat(high.toFixed(5)),
      low: parseFloat(low.toFixed(5)),
      close: parseFloat(close.toFixed(5)),
      volume: Math.round(volume),
      trades: dayTrades.length,
      pnl: dailyPnL,
      bullish: close >= open,
    });

    basePrice = close;
  });

  return data.length > 0 ? data : generateRandomOHLC(pair, timeframe);
}

// Generate random OHLC data as fallback
function generateRandomOHLC(pair, timeframe) {
  const days = timeframe === "1D" ? 24 : timeframe === "1W" ? 7 : 30;
  const data = [];
  let basePrice = getBasePrice(pair);

  for (let i = days; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split("T")[0];

    const volatility = basePrice * (0.002 + Math.random() * 0.005);
    const open = basePrice;
    const close = open + (Math.random() - 0.48) * volatility;
    const high = Math.max(open, close) + Math.random() * volatility * 0.5;
    const low = Math.min(open, close) - Math.random() * volatility * 0.5;
    const volume = Math.round(500 + Math.random() * 2000);

    data.push({
      date: dateStr,
      open: parseFloat(open.toFixed(5)),
      high: parseFloat(high.toFixed(5)),
      low: parseFloat(low.toFixed(5)),
      close: parseFloat(close.toFixed(5)),
      volume,
      trades: Math.floor(Math.random() * 5) + 1,
      pnl: parseFloat(((close - open) * 10000).toFixed(2)),
      bullish: close >= open,
    });

    basePrice = close;
  }

  return data;
}

function getBasePrice(pair) {
  const prices = {
    "EUR/USD": 1.085,
    "GBP/USD": 1.27,
    "USD/JPY": 154.5,
    "USD/CHF": 0.89,
    "AUD/USD": 0.66,
    "NZD/USD": 0.61,
    "USD/CAD": 1.36,
    "EUR/GBP": 0.855,
    "EUR/JPY": 167.5,
    "GBP/JPY": 196.2,
    "XAU/USD": 2350,
    "BTC/USD": 68500,
  };
  return prices[pair] || 1.0;
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <div className="bg-card border border-border rounded-xl p-3 shadow-xl min-w-[200px]">
      <p className="text-xs font-semibold text-muted-foreground mb-2">{label}</p>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-4">
          <span className="text-xs text-muted-foreground">Open</span>
          <span className="text-xs font-mono font-semibold">{data.open}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-xs text-muted-foreground">High</span>
          <span className="text-xs font-mono font-semibold text-success">{data.high}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-xs text-muted-foreground">Low</span>
          <span className="text-xs font-mono font-semibold text-destructive">{data.low}</span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-xs text-muted-foreground">Close</span>
          <span
            className={`text-xs font-mono font-bold ${
              data.bullish ? "text-success" : "text-destructive"
            }`}
          >
            {data.close}
          </span>
        </div>
        <div className="border-t border-border pt-1.5 mt-1.5">
          <div className="flex items-center justify-between gap-4">
            <span className="text-xs text-muted-foreground">P/L</span>
            <span
              className={`text-xs font-bold ${
                data.pnl >= 0 ? "text-success" : "text-destructive"
              }`}
            >
              {data.pnl >= 0 ? "+" : ""}
              {data.pnl.toFixed(2)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-xs text-muted-foreground">Trades</span>
            <span className="text-xs font-semibold">{data.trades}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Custom candlestick shape using bars
function CandlestickBar(props) {
  const { x, y, width, height, payload } = props;
  if (!payload) return null;

  const { open, close, high, low, bullish } = payload;
  const color = bullish ? "#22c55e" : "#ef4444";
  const bodyColor = bullish ? "#22c55e" : "#ef4444";

  // Calculate positions relative to the chart
  const yScale = props.yAxis;
  if (!yScale) return null;

  const yOpen = yScale.scale(open);
  const yClose = yScale.scale(close);
  const yHigh = yScale.scale(high);
  const yLow = yScale.scale(low);

  const bodyTop = Math.min(yOpen, yClose);
  const bodyHeight = Math.max(Math.abs(yOpen - yClose), 1);

  const centerX = x + width / 2;

  return (
    <g>
      {/* Wick (high-low line) */}
      <line
        x1={centerX}
        y1={yHigh}
        x2={centerX}
        y2={yLow}
        stroke={color}
        strokeWidth={1.5}
      />
      {/* Body */}
      <rect
        x={x + 2}
        y={bodyTop}
        width={width - 4}
        height={bodyHeight}
        fill={bodyColor}
        opacity={0.85}
        rx={1}
      />
    </g>
  );
}

export default function TradingChart({
  trades = [],
  selectedPair: controlledPair,
  className = "",
}) {
  const [pair, setPair] = useState(controlledPair || "EUR/USD");
  const [timeframe, setTimeframe] = useState("1M");

  // Use controlled pair if provided
  const activePair = controlledPair || pair;

  const ohlcData = useMemo(() => {
    return generateOHLCData(trades, activePair, timeframe);
  }, [trades, activePair, timeframe]);

  // Compute summary stats
  const stats = useMemo(() => {
    if (ohlcData.length === 0) return null;
    const first = ohlcData[0];
    const last = ohlcData[ohlcData.length - 1];
    const change = last.close - first.open;
    const changePercent = ((change / first.open) * 100).toFixed(2);
    const totalVolume = ohlcData.reduce((sum, d) => sum + d.volume, 0);
    const bullishCount = ohlcData.filter((d) => d.bullish).length;
    const bearishCount = ohlcData.length - bullishCount;

    return {
      currentPrice: last.close,
      change,
      changePercent: parseFloat(changePercent),
      totalVolume,
      bullishCount,
      bearishCount,
      highest: Math.max(...ohlcData.map((d) => d.high)),
      lowest: Math.min(...ohlcData.map((d) => d.low)),
    };
  }, [ohlcData]);

  return (
    <Card className={`shadow-lg border-border/50 ${className}`} data-testid="trading-chart">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-[#1877F2]" />
              Price Action
            </CardTitle>
            <CardDescription>Candlestick view for {activePair}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={activePair} onValueChange={setPair}>
              <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="chart-pair-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {POPULAR_PAIRS.map((p) => (
                  <SelectItem key={p} value={p} className="text-xs">
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center bg-secondary/50 rounded-lg p-0.5">
              {TIMEFRAMES.map((tf) => (
                <Button
                  key={tf.value}
                  variant={timeframe === tf.value ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setTimeframe(tf.value)}
                  className="h-7 px-3 text-xs rounded-md"
                  data-testid={`chart-tf-${tf.value}`}
                >
                  {tf.label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Price Summary */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4 mt-2"
          >
            <span className="text-2xl font-bold font-mono">{stats.currentPrice}</span>
            <Badge
              variant={stats.change >= 0 ? "default" : "destructive"}
              className={`text-xs ${
                stats.change >= 0
                  ? "bg-success/15 text-success border-success/30"
                  : "bg-destructive/15 text-destructive border-destructive/30"
              }`}
            >
              {stats.change >= 0 ? (
                <TrendingUp className="h-3 w-3 mr-1" />
              ) : (
                <TrendingDown className="h-3 w-3 mr-1" />
              )}
              {stats.change >= 0 ? "+" : ""}
              {stats.changePercent}%
            </Badge>
            <span className="text-xs text-muted-foreground hidden sm:inline">
              H: {stats.highest} · L: {stats.lowest}
            </span>
          </motion.div>
        )}
      </CardHeader>

      <CardContent>
        <div style={{ height: 320 }}>
          {ohlcData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={ohlcData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                <XAxis
                  dataKey="date"
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(val) => {
                    if (!val) return "";
                    const d = new Date(val);
                    return `${d.getMonth() + 1}/${d.getDate()}`;
                  }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  yAxisId="price"
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fontSize: 10 }}
                  domain={["auto", "auto"]}
                  tickFormatter={(val) => val.toFixed(val >= 100 ? 1 : 4)}
                />
                <YAxis
                  yAxisId="volume"
                  orientation="right"
                  stroke="hsl(var(--muted-foreground))"
                  tick={{ fontSize: 10 }}
                  hide
                />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine
                  yAxisId="price"
                  y={ohlcData[0]?.open}
                  stroke="hsl(var(--muted-foreground))"
                  strokeDasharray="5 5"
                  opacity={0.3}
                />

                {/* Volume bars */}
                <Bar
                  yAxisId="volume"
                  dataKey="volume"
                  fill="hsl(var(--primary))"
                  opacity={0.08}
                  radius={[2, 2, 0, 0]}
                />

                {/* Candlestick bodies as colored bars */}
                <Bar
                  yAxisId="price"
                  dataKey={(entry) => Math.abs(entry.close - entry.open)}
                  shape={<CandlestickBar yAxis={(d) => d} />}
                  isAnimationActive={false}
                >
                  {ohlcData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.bullish ? "#22c55e" : "#ef4444"}
                      opacity={0.85}
                    />
                  ))}
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <div className="text-center">
                <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No price data available</p>
                <p className="text-xs mt-1">Record trades to see chart data</p>
              </div>
            </div>
          )}
        </div>

        {/* Legend */}
        {stats && (
          <div className="flex items-center justify-center gap-4 mt-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <div className="h-2.5 w-2.5 rounded-sm bg-success" />
              <span>Bullish ({stats.bullishCount})</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="h-2.5 w-2.5 rounded-sm bg-destructive" />
              <span>Bearish ({stats.bearishCount})</span>
            </div>
            <span>·</span>
            <span>Vol: {stats.totalVolume.toLocaleString()}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
